import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarCheck, Loader2, ArrowRight, Search } from "lucide-react";
import { GlassCard, StatusBadge } from "../../components/common/ui";
import { Input } from "../../components/ui/input";
import { formatGBP } from "../../components/common/StockBadge";
import api from "../../lib/api";

const STATUS_LABEL = {
  RESERVATION_RECEIVED: "Received", UNDER_REVIEW: "Under Review", CONFIRMED: "Confirmed",
  READY_FOR_COLLECTION: "Ready for Collection", COLLECTED: "Collected", CANCELLED: "Cancelled",
};
const selectCls = "bg-black/30 border border-white/15 text-white rounded-md px-3 py-2 text-sm";
const daysUntil = (d) => {
  if (!d) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const target = new Date(d + "T00:00:00");
  const n = Math.round((target - today) / 86400000);
  return n < 0 ? "Collection date passed" : n === 0 ? "Collection today" : n === 1 ? "Collection tomorrow" : `${n} days until collection`;
};

export default function Reservations() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("newest");

  useEffect(() => { api.get("/me/reservations").then(({ data }) => setItems(data)).catch(() => setItems([])); }, []);

  const filtered = useMemo(() => {
    let list = [...(items || [])];
    if (q.trim()) list = list.filter((r) => r.reservation_number.toLowerCase().includes(q.trim().toLowerCase()));
    if (status) list = list.filter((r) => r.status === status);
    list.sort((a, b) => sort === "newest" ? (a.created_at < b.created_at ? 1 : -1) : (a.created_at > b.created_at ? 1 : -1));
    return list;
  }, [items, q, status, sort]);

  return (
    <div className="space-y-6" data-testid="customer-reservations">
      <div>
        <h1 className="font-playfair text-2xl sm:text-3xl text-white">My Reservations</h1>
        <p className="mt-2 text-muted-foreground">Track the status of your Click & Collect reservations.</p>
      </div>

      {items === null ? (
        <div className="py-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-gold" /></div>
      ) : items.length === 0 ? (
        <GlassCard className="p-12 flex flex-col items-center text-center" hover={false} data-testid="reservations-empty">
          <div className="grid place-items-center h-14 w-14 rounded-2xl bg-gold/10 border border-gold/20"><CalendarCheck className="h-7 w-7 text-gold" /></div>
          <p className="mt-5 font-playfair text-xl text-white">No reservations yet</p>
          <p className="mt-2 text-sm text-muted-foreground max-w-md">Reserve fireworks online and they'll appear here with live status tracking.</p>
          <Link to="/fireworks" className="mt-5"><span className="cf-gold-btn inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold">Browse Fireworks <ArrowRight className="h-4 w-4" /></span></Link>
        </GlassCard>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
              <Input data-testid="reservations-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by reservation number…" className="pl-9 bg-black/30 border-white/15 text-white" />
            </div>
            <select data-testid="reservations-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className={selectCls}>
              <option value="">All statuses</option>
              {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select data-testid="reservations-sort" value={sort} onChange={(e) => setSort(e.target.value)} className={selectCls}>
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
            </select>
          </div>

          {filtered.length === 0 ? (
            <GlassCard className="p-8 text-center text-muted-foreground" hover={false}>No reservations match your filters.</GlassCard>
          ) : (
            <div className="space-y-3" data-testid="reservations-list">
              {filtered.map((r) => (
                <Link key={r.reservation_number} to={`/reservation/${r.reservation_number}`} data-testid={`reservation-row-${r.reservation_number}`}
                      className={`flex items-center justify-between gap-4 rounded-xl border bg-white/5 p-4 transition-colors ${r.status === "READY_FOR_COLLECTION" ? "border-emerald-500/40 hover:border-emerald-400/60" : "border-white/10 hover:border-gold/40"}`}>
                  <div className="min-w-0">
                    <p className="text-gold font-semibold">{r.reservation_number}</p>
                    {r.status === "READY_FOR_COLLECTION" && <p className="text-emerald-300 text-sm font-medium">Ready for collection</p>}
                    <p className="text-sm text-white/60">Reserved {(r.created_at || "").slice(0, 10)} · Collection {r.collection_date} · {r.items?.length || 0} item{(r.items?.length || 0) !== 1 ? "s" : ""}</p>
                    {["RESERVATION_RECEIVED", "UNDER_REVIEW", "CONFIRMED", "READY_FOR_COLLECTION"].includes(r.status) && <p className="text-xs text-white/40">{daysUntil(r.collection_date)}</p>}
                  </div>
                  <div className="text-right shrink-0">
                    <StatusBadge status={STATUS_LABEL[r.status] || "Received"} />
                    <p className="mt-1 text-white font-semibold text-sm">{formatGBP(r.estimated_total)}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
